import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { Facility, Repayment } from './shared/models/models';
import {
  PaymentDetailsResponse,
  PaymentScheduleDetail,
  PaymentDetail,
  DisbursementSummary,
} from './shared/models/payment-details.model';
import { DisbursementService } from './shared/service/disbursement.service';
import { Setup } from '../setup/shared/service/setup';
import { Popup } from '../../core/shared/components/popup/popup';

import { PickListService } from '../../core/shared/services/picklist.service';
import { PKL_GRP } from '../../core/shared/models/picklist.model';

import { DynamicFieldConfig } from '../../core/shared/dynamic-form/form-config.model';
import { Table, pageWindow } from '../shared/table/table';
import { TableColumn, TableAction } from '../shared/models/table-models';
import { faCalendar, faMoneyBill, faPencil, faTrash } from '@fortawesome/free-solid-svg-icons';
import { ToastService } from '../../core/shared/services/toast.service';
import { from, of, concatMap, catchError, map, toArray, BehaviorSubject } from 'rxjs';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-disbursement',
  standalone: true,
  imports: [CommonModule, FormsModule, Popup, Table, TranslatePipe],
  templateUrl: './disbursement.html',
  styleUrl: './disbursement.css',
})
export class Disbursement implements OnInit {
  facilityPopupFields: DynamicFieldConfig[] = [];
  disbursementPopupFields: DynamicFieldConfig[] = [];
  showPendingSchedules = false;
  pendingScheduleList: any[] = [];
  // Table Config
  facilityTableColumns: TableColumn[] = [];
  disbursementTableColumns: TableColumn[] = [];
  disbursementTableActions: TableAction[] = [];
  PKL_GRP = PKL_GRP;
  showPenaltyModal = false;
  penaltyList: any[] = [];
  penaltyTableColumns: TableColumn[] = [];
  // Schedule Config
  faInfoCircle = faInfoCircle;
  showScheduleList = false;
  showEditSchedule = false;
  isScheduleDirty = false;
  scheduleList: any[] = [];
  selectedSchedule: any = {};
  schedulePopupFields: DynamicFieldConfig[] = [];

  scheduleTableColumns: TableColumn[] = [];
  scheduleTableActions: TableAction[] = [];

  // Payment Details (New Rich Endpoint)
  paymentDetails: PaymentDetailsResponse | null = null;
  disbursementSummary: DisbursementSummary = this.createEmptyDisbursementSummary();
  expandedScheduleIds = new Set<number>();
  showRestructureConfirm = false;
  // Repayment Config
  repaymentTableColumns: TableColumn[] = [];
  repaymentTableActions: TableAction[] = [];
  showAccruedMarkupTable = false;
  accruedMarkupList: any[] = [];
  accruedMarkupColumns: TableColumn[] = [];
  accruedMarkupActions: TableAction[] = [];
  showAccruedMarkupPaymentModal = false;
  selectedAccruedMarkupPayment: any = {};
  accruedMarkupPaymentPopupFields: DynamicFieldConfig[] = [];
  showDeleteConfirmModal = false;
  selectedDisbursementForDelete: any = null;
  constructor(
    public disbursementService: DisbursementService,
    private setupService: Setup,
    private pickListService: PickListService,
    private toast: ToastService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.initTableConfig();
    this.loadFacilities();
    this.loadClients();
  }

  // ================= SELECTION & FILTER =================
  @ViewChild('mainTable') mainTable!: Table;

  selectedClientId: number | null = null;
  selectedFacilityIds: number[] = [];
  selectedFacilities: Facility[] = [];

  get visibleFacilities(): Facility[] {
    if (!this.selectedClientId) {
      return this.facilities;
    }
    return this.facilities.filter((f) => f.clientSeq === Number(this.selectedClientId));
  }
  isRestructured(row: any): boolean {
    return Number(row.status) === 23; // <-- confirm 8 = Restructured
  }
  onClientChange(): void {
    if (this.mainTable) {
      this.mainTable.clearSelection();
    }
    this.selectedFacilityIds = [];
    this.selectedFacilities = [];
    this.currentPage = 1;
  }

  openPendingSchedules(): void {
    const fcltySeq = this.selectedFacility?.id;

    if (!fcltySeq) {
      this.toast.error('DISBURSEMENT.FACILITY_NOT_SELECTED');
      return;
    }

    this.loading = true;

    this.disbursementService.getPendingSchedulesByFacility(fcltySeq).subscribe({
      next: (res: any) => {
        // ✅ Check for error in response body
        if (res?.error) {
          this.loading = false;
          this.toast.error(res.error);
          return;
        }
        const list = Array.isArray(res) ? res : res.data || [];

        this.pendingScheduleList = list;

        this.showPendingSchedules = true;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.toast.error('DISBURSEMENT.FAILED_LOAD_PENDING_SCHEDULES');
      },
    });
  }
  pendingScheduleColumns: TableColumn[] = [];

  pendingScheduleInnerColumns: TableColumn[] = [
    { key: 'fcltyNm', label: 'DISBURSEMENT.FACILITY', type: 'text' },
    { key: 'instNum', label: 'DISBURSEMENT.INSTALLMENT', type: 'text' },
    { key: 'dueDt', label: 'DISBURSEMENT.DUE_DATE', type: 'date' },
    { key: 'dueAmt', label: 'DISBURSEMENT.DUE_AMOUNT', type: 'currency' },
    { key: 'paidAmt', label: 'DISBURSEMENT.PAID_AMOUNT', type: 'currency' },
  ];
  expandedPendingRows = new Set<string>();

  togglePendingRow(month: string): void {
    if (this.expandedPendingRows.has(month)) {
      this.expandedPendingRows.delete(month);
    } else {
      this.expandedPendingRows.add(month);
    }
  }

  onSelectionChange(selectedRows: any[]): void {
    this.selectedFacilities = selectedRows;
    this.selectedFacilityIds = selectedRows.map((r) => r.id);
  }
  openRestructureConfirm(): void {
    if (this.selectedFacilities.length === 0) {
      this.toast.error('DISBURSEMENT.SELECT_AT_LEAST_ONE_FACILITY');
      return;
    }

    this.showRestructureConfirm = true;
  }
  confirmRestructure(): void {
    if (this.selectedFacilities.length === 0) {
      this.toast.error('DISBURSEMENT.SELECT_AT_LEAST_ONE_FACILITY');
      return;
    }

    // Check 1: Same Client
    const firstClient = this.selectedFacilities[0].clientSeq;
    const sameClient = this.selectedFacilities.every((f) => f.clientSeq === firstClient);
    if (!sameClient) {
      this.toast.error(
        'DISBURSEMENT.RESTRUCTURE_FAILED_SAME_CLIENT',
      );
      return;
    }

    // Check 2: Same Facility Type
    const firstType = this.selectedFacilities[0].fcltyTypKey;
    const sameType = this.selectedFacilities.every((f) => f.fcltyTypKey === firstType);
    if (!sameType) {
      this.toast.error(
        'DISBURSEMENT.RESTRUCTURE_FAILED_SAME_TYPE',
      );
      return;
    }

    this.loading = true;
    this.disbursementService.sendSelectedFacilities(this.selectedFacilityIds).subscribe({
      next: (res) => {
        this.loading = false;
        if (res.error) {
          this.toast.error(res.error);
        } else {
          this.toast.success('DISBURSEMENT.PROCESSED_SUCCESSFULLY');
          this.selectedFacilityIds = [];
          this.selectedFacilities = [];
          if (this.mainTable) {
            this.mainTable.clearSelection();
          }
          this.loadFacilities();
        }
      },
      error: (err) => {
        this.loading = false;
        console.error('Process failed', err);

        // Error is now normalized by service
        this.toast.error(err.message || 'DISBURSEMENT.FAILED_PROCESS_SELECTION');
      },
    });
  }
  // ================= CONSTANT DATA =================

  clients: { clientSeq: number; name: string }[] = [];
  clients$ = new BehaviorSubject<{ label: string; value: number }[]>([]);

  loadClients(): void {
    this.setupService.getclientList().subscribe({
      next: (res: any) => {
        const list = Array.isArray(res) ? res : res.data || [];
        this.clients = list.map((c: any) => ({
          clientSeq: Number(c.clientSeq),
          name: c.clntNm,
        }));
        this.clients$.next(this.clients.map((c) => ({ label: c.name, value: c.clientSeq })));
      },
      error: (err) => console.error('Client load failed', err),
    });
  }

  banks: string[] = ['HBL', 'UBL', 'MCB', 'Standard Chartered', 'Bank Alfalah', 'Meezan Bank'];

  // ================= PAGINATION =================
  pageSize = 12;
  currentPage = 1;

  get totalRecords(): number {
    return this.visibleFacilities.length;
  }

  get startRecord(): number {
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get endRecord(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalRecords);
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.totalRecords / this.pageSize));
  }

  get pages(): (number | null)[] {
    return pageWindow(this.currentPage, this.totalPages);
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
    }
  }

  get paginatedFacilities(): Facility[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.visibleFacilities.slice(start, start + this.pageSize);
  }

  nextPage(): void {
    if (this.endRecord < this.totalRecords) {
      this.currentPage++;
    }
  }

  prevPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
    }
  }

  // ================= UI STATE =================
  showEditFacility = false;
  showRepaymentList = false;
  showEditRepayment = false;
  showAssuredIncom = false;
  showDisbursementModal = false;
  showDisbursementList = false;
  showCalculateAccruedModal = false;
  calculationMonthYear: string = '';

  isCreateMode = false;
  isRepaymentCreateMode = false;

  // ================= DATA =================
  facilities: Facility[] = [];
  disbursementList: any[] = [];

  loading = false;

  repayments: Repayment[] = [
    {
      id: 1,
      installment: 1,
      date: '2024-12-12',
      amount: 200000,
      status: 'Draft',
      transactionNo: 'TXN-001',
    },
    {
      id: 2,
      installment: 2,
      date: '2025-01-12',
      amount: 300000,
      status: 'Paid',
      transactionNo: 'TXN-002',
    },
  ];

  selectedFacility!: Facility;
  selectedRepayment!: Repayment;
  selectedDisbursement: any = {};

  // ================= HELPER MAPS =================
  getPicklistMap(groupCode: number): Record<string | number, string> {
    const options = this.pickListService.getOptionsSync(groupCode);
    const map: Record<string | number, string> = {};
    options.forEach((opt) => {
      map[opt.value] = opt.label;
    });
    return map;
  }

  initTableConfig() {
    this.facilityTableColumns = [
      { label: 'DISBURSEMENT.FACILITY', key: 'facilityName', type: 'text' },
      {
        label: 'DISBURSEMENT.FACILITY_TYPE',
        key: 'fcltyTypKey',
        type: 'map',
        map: this.getPicklistMap(PKL_GRP.FACILITY_TYPE),
      },
      {
        label: 'DISBURSEMENT.PHASE',
        key: 'phase',
        type: 'text',
      },
      { label: 'DISBURSEMENT.TOTAL_EXPOSURE', key: 'amount', type: 'currency' },
      { label: 'DISBURSEMENT.REMAINING_EXPOSURE', key: 'remainingAmount', type: 'currency' },

      { label: 'COMMON.DATE', key: 'date', type: 'date' },
      {
        label: 'COMMON.STATUS',
        key: 'status',
        type: 'map',
        map: this.getPicklistMap(PKL_GRP.STATUS),
      },
    ];
    this.penaltyTableColumns = [
      { key: 'instNum', label: 'DISBURSEMENT.INSTALLMENT', type: 'text' },
      { key: 'dueDt', label: 'DISBURSEMENT.DUE_DATE', type: 'date' },
      { key: 'dueAmt', label: 'DISBURSEMENT.DUE_AMOUNT', type: 'currency' },
      { key: 'pnltyAmnt', label: 'DISBURSEMENT.PENALTY_AMOUNT', type: 'currency' },
      { key: 'pnltyPaidAmt', label: 'DISBURSEMENT.PENALTY_PAID', type: 'currency' },
      { key: 'rmngAmt', label: 'DISBURSEMENT.REMAINING', type: 'currency' },
      { key: 'pnltyPaidDt', label: 'DISBURSEMENT.PENALTY_PAID_DATE', type: 'date' },
    ];

    this.accruedMarkupColumns = [
      { key: 'monthYearOfCalc', label: 'DISBURSEMENT.MONTH', type: 'text' },
      { key: 'dsbmntOutStndng', label: 'DISBURSEMENT.OUTSTANDING_DISBURSEMENT', type: 'currency' },
      { key: 'accrudAmnt', label: 'DISBURSEMENT.ACCRUED_AMOUNT', type: 'currency' },
      { key: 'sprdRt', label: 'DISBURSEMENT.SPREAD_RATE', type: 'text' },
      { key: 'kbrRt', label: 'DISBURSEMENT.KIBOR_RATE', type: 'text' },
      {
        key: 'stsKey',
        label: 'COMMON.STATUS',
        type: 'map',
        map: this.getPicklistMap(PKL_GRP.DISBURSEMENT_PAYMENT_TYPE),
      },
    ];

    this.accruedMarkupActions = [
      {
        icon: faMoneyBill,
        onClick: (row) => this.openAccruedMarkupPayment(row),
        class:
          'h-8 w-8 bg-purple-100 text-purple-500 rounded-full flex items-center justify-center font-bold',
        tooltip: 'DISBURSEMENT.PAY',
        hide: (row) => row.stsKey === 8,
      },
    ];
    this.disbursementTableColumns = [
      { key: 'dsbmntDt', label: 'COMMON.DATE', type: 'date' },
      { key: 'dsbmntAmt', label: 'COMMON.AMOUNT', type: 'currency' },
      {
        key: 'instFrqncy',
        label: 'DISBURSEMENT.FREQUENCY',
        type: 'map',
        map: this.getPicklistMap(PKL_GRP.DISBURSEMENT_FREQUENCY),
      },
      {
        key: 'stsKey',
        label: 'COMMON.STATUS',
        type: 'map',
        map: this.getPicklistMap(PKL_GRP.STATUS),
      },
    ];

    this.disbursementTableActions = [
      {
        icon: faCalendar,
        onClick: (row) => this.openSchedule(row),
        class:
          'h-8 w-8 text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg flex items-center justify-center',
        tooltip: 'DISBURSEMENT.SCHEDULE',
      },
      {
        icon: '/public/3.png',
        onClick: (row) => this.onDisbrestmentAccourd(row),
        class: 'h-8 w-8 rounded-lg flex items-center justify-center hover:bg-gray-100',
        tooltip: 'DISBURSEMENT.ACCRUED_MARKUP',
      },
      {
        icon: faTrash,
        onClick: (row) => this.openDeleteConfirm(row),
        class:
          'h-8 w-8 text-red-600 bg-red-50 hover:bg-red-100 rounded-lg flex items-center justify-center',
        tooltip: 'COMMON.DELETE',
      },
    ];
    this.pendingScheduleColumns = [
      {
        label: 'DISBURSEMENT.MONTH',
        key: 'month',
        type: 'text',
      },
      {
        label: 'DISBURSEMENT.CALCULATION_MONTH',
        key: 'monthYearOfCalc',
        type: 'text',
      },
      {
        label: 'DISBURSEMENT.TOTAL_AMOUNT',
        key: 'totalAmount',
        type: 'currency',
      },
      {
        label: 'DISBURSEMENT.SCHEDULES',
        key: 'numberOfSchedules',
        type: 'text',
      },
      {
        label: 'COMMON.STATUS',
        key: 'stsKey',
        type: 'map',
        map: this.getPicklistMap(PKL_GRP.DISBURSEMENT_PAYMENT_TYPE),
      },
    ];
    // --- Schedule Table Config ---
    this.scheduleTableColumns = [
      { key: 'instNum', label: 'DISBURSEMENT.INSTALLMENT_NO', type: 'text' },
      { key: 'dueDt', label: 'DISBURSEMENT.DUE_DATE', type: 'date' },
      { key: 'dueAmt', label: 'DISBURSEMENT.DUE_AMOUNT', type: 'currency' },
      { key: 'paidAmt', label: 'DISBURSEMENT.PAID_AMOUNT', type: 'currency' },
      { key: 'paidDt', label: 'DISBURSEMENT.PAID_DATE', type: 'date' },
      {
        key: 'stsKey',
        label: 'COMMON.STATUS',
        type: 'map',
        map: this.getPicklistMap(PKL_GRP.DISBURSEMENT_PAYMENT_TYPE),
      },
    ];

    this.scheduleTableActions = [
      {
        icon: faMoneyBill,
        onClick: (row) => this.openSchedulePayment(row),
        class:
          'h-8 w-8 bg-purple-100 text-purple-500 rounded-full flex items-center justify-center font-bold',
        tooltip: 'DISBURSEMENT.PAY',
        hide: (row) => row.stsKey == 8,
      },
      {
        icon: faPencil,
        onClick: (row) => this.openEditSchedule(row),
        class:
          'h-8 w-8 text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg flex items-center justify-center',
        tooltip: 'DISBURSEMENT.EDIT_INSTALLMENT',
      },
    ];

    // ================= REPAYMENT LIST CONFIG =================
    this.repaymentTableColumns = [
      { label: 'DISBURSEMENT.MONTH', key: 'month', type: 'text' },

      { label: 'DISBURSEMENT.PAYMENT_DATE', key: 'paymentDate', type: 'date' },

      { label: 'DISBURSEMENT.TOTAL_AMOUNT', key: 'totalAmount', type: 'currency' },

      { label: 'DISBURSEMENT.DUE_AMOUNT', key: 'totalDueAmt', type: 'currency' },

      { label: 'DISBURSEMENT.NO_OF_INSTALLMENTS', key: 'numberOfInstallments', type: 'text' },

      {
        label: 'COMMON.STATUS',
        key: 'stsKey',
        type: 'map',
        map: this.getPicklistMap(PKL_GRP.DISBURSEMENT_PAYMENT_TYPE),
      },
    ];

    this.repaymentTableActions = [
      {
        icon: faMoneyBill,
        onClick: (row) => this.openRepaymentPayment(row),
        class:
          'h-8 w-8 bg-purple-100 text-purple-500 hover:bg-purple-200 flex items-center justify-center',
        tooltip: 'DISBURSEMENT.PAY',

        // ✅ simple + safe
        hide: (row) => this.isRestructuredFacility() || row.stsKey === 8,
      },
    ];
  }
  isRestructuredFacility(): boolean {
    return Number(this.selectedFacility?.status) === 23;
  }
  onPaymentClick(row: any): void {}

  loadFacilities(): void {
    this.loading = true;

    this.disbursementService.getFacilities().subscribe({
      next: (res: any) => {
        const list = Array.isArray(res) ? res : res.data || [];

        this.facilities = list.map((item: any) => ({
          id: item.fcltySeq,
          facilityName: (item.fcltyNm || '').trim(),
          // keep numeric for form editing
          phase: Number(item.phsKey),

          amount: Number(item.fcltyAprvdAmt),
          remainingAmount: Number(item.remainingAmount ?? 0),

          fcltyDsprdAmt: Number(item.fcltyDsprdAmt ?? 0),
          dsbmntFlg: Number(item.dsbmntFlg ?? 0),
          markupFlg: Number(item.markupFlg ?? 0),

          date: this.fromIsoToDate(item.aprvlDt),
          fcltyEndDt: this.fromIsoToDate(item.fcltyEndDt),

          status: Number(item.stsKey),
          fcltyTypKey: item.fcltyTypKey,

          clientSeq:
            item.client?.clientSeq || item.clientSeq || item.clntSeq
              ? Number(item.client?.clientSeq || item.clientSeq || item.clntSeq)
              : null,

          spreadRate: Number(item.sprdRt ?? 0),
          kiborRate: Number(item.kbrRt ?? 0),
          kiborResetPeriod: Number(item.kbrRstPrd ?? 0),

          installmentCount: Number(item.noInstlmnt ?? 1),
          gracePeriod: Number(item.grcPrd ?? 0),
          installmentFrequency: this.mapFrequencyDisplay(item.instFrqncy),

          // include accrued fields if returned
          acrdCalcFlg: Number(item.acrdCalcFlg ?? 0),
          acrdCalcDay: item.acrdCalcDay ?? 0,

          // V-KIBOR Fields (Variable KIBOR per day)
          vkbrFlg: Number(item.vkbrFlg ?? 0),
          vkbrRt: item.vkbrRt ?? null,
          vkbrStrtDt: this.fromIsoToDate(item.vkbrStrtDt),
          vkbrEndDt: this.fromIsoToDate(item.vkbrEndDt),
        }));

        // ✅ Sort: Letters first (A-Z), then numbers
        this.facilities.sort((a, b) => {
          const nameA = (a.facilityName || '').trim();
          const nameB = (b.facilityName || '').trim();

          const aStartsWithNumber = /^\d/.test(nameA);
          const bStartsWithNumber = /^\d/.test(nameB);

          if (aStartsWithNumber && !bStartsWithNumber) return 1;
          if (!aStartsWithNumber && bStartsWithNumber) return -1;

          return nameA.localeCompare(nameB, undefined, {
            sensitivity: 'base',
            numeric: true,
          });
        });

        this.loading = false;
        this.loading = false;
      },
      error: () => {
        this.facilities = [];
        this.loading = false;
      },
    });
  }

  onFacilityPopupSave = (data: any) => {
    // ✅ Always set acrdCalcFlg to 0 (not user input)
    data.acrdCalcFlg = 0;

    // ✅ If V-KIBOR is OFF (vkbrFlg=0): clear V-KIBOR details
    if (Number(data.vkbrFlg) === 0) {
      data.vkbrRt = null;
      data.vkbrStrtDt = null;
      data.vkbrEndDt = null;
    }

    this.selectedFacility = {
      ...this.selectedFacility,
      ...data,
    };

    this.saveFacility();
  };

  private fromIsoToDate(iso?: string | null): string | null {
    if (!iso) return null;
    return iso.split('T')[0]; // yyyy-MM-dd
  }

  private mapFrequencyDisplay(key?: number | null): string | undefined {
    const map: Record<number, string> = {
      1: 'Monthly',
      2: 'Quarterly',
    };
    return key ? map[key] : undefined;
  }

  private createEmptyFacility(): Facility {
    return {
      id: 0,
      facilityName: '',
      phase: 0,
      amount: 0,
      date: null,
      status: 0,

      clientSeq: null,
      fcltyTypKey: undefined,

      gracePeriod: 0,
      installmentCount: 0,
      spreadRate: 0,
      kiborResetPeriod: 0,
      kiborRate: 0,

      // V-KIBOR Fields
      vkbrFlg: 0,
      vkbrRt: null,
      vkbrStrtDt: null,
      vkbrEndDt: null,

      fcltyEndDt: null,
      fcltyDsprdAmt: 0,
      dsbmntFlg: 0,
      markupFlg: 0,

      acrdCalcFlg: 0,
      acrdCalcDay: 0,
    };
  }

  private createEmptyRepayment(): Repayment {
    return {
      id: Date.now(),
      installment: this.repayments.length + 1,
      date: '',
      amount: 0,
      status: 'Draft',
      transactionNo: '',
      bank: '',
    };
  }

  private createEmptyDisbursementSummary(): DisbursementSummary {
    return {
      facilityName: '',
      disbursementDate: '',
      disbursementAmount: 0,
      totalSchedules: 0,
      paidSchedules: 0,
      pendingSchedules: 0,
      totalPaidAmount: 0,
      totalRemainingAmount: 0,
      fullyPaid: false,
      progressPercentage: 0,
    };
  }

  private resetScheduleModalState(): void {
    this.paymentDetails = null;
    this.scheduleList = [];
    this.selectedSchedule = {};
    this.selectedSchedulePayment = {};
    this.expandedScheduleIds.clear();
    this.isScheduleDirty = false;
    this.disbursementSummary = this.createEmptyDisbursementSummary();
  }

  canEditSchedule(schedule: any): boolean {
    return schedule.stsKey !== 8 && !this.isScheduleLocked();
  }

  isScheduleLocked(): boolean {
    return Number(this.selectedFacility?.status) === 23;
  }
  onScheduleModalVisibilityChange(show: boolean): void {
    this.showScheduleList = show;

    if (!show) {
      this.resetScheduleModalState();
    }
  }

  // ================= CALCULATE ACCRUED =================
  openCalculateAccrued(): void {
    this.showCalculateAccruedModal = true;
    this.calculationMonthYear = '';
  }

  calculateAccrued(): void {
    if (!this.calculationMonthYear) {
      this.toast.error('DISBURSEMENT.SELECT_MONTH_AND_YEAR_REQUIRED');
      return;
    }

    const payload = {
      monthYearOfCalc: this.calculationMonthYear,
    };

    this.loading = true;
    this.disbursementService.calculateAccruedMarkup(payload).subscribe({
      next: () => {
        this.loading = false;
        this.showCalculateAccruedModal = false;
        this.toast.success('DISBURSEMENT.ACCRUED_MARKUP_CALCULATED');
      },
      error: (err) => {
        console.error('Calculate Accrued Failed', err);
        this.loading = false;
        this.toast.error('DISBURSEMENT.CALCULATE_ACCRUED_FAILED');
      },
    });
  }

  // ================= FACILITY ACTIONS =================
  addNewFacility(): void {
    this.isCreateMode = true;
    this.selectedFacility = this.createEmptyFacility();
    this.prepareFacilityPopupFields();
    this.showEditFacility = true;
  }

  openEditFacility(row: Facility): void {
    this.isCreateMode = false;
    this.selectedFacility = { ...row };
    this.prepareFacilityPopupFields();
    this.showEditFacility = true;
  }

  prepareFacilityPopupFields(): void {
    this.facilityPopupFields = [
      {
        key: 'clientSeq',
        label: 'DISBURSEMENT.CLIENT',
        type: 'select',
        value: this.selectedFacility.clientSeq,
        options$: this.clients$,
        validations: { required: true },
      },
      {
        key: 'facilityName',
        label: 'DISBURSEMENT.FACILITY',
        type: 'text',
        value: this.selectedFacility.facilityName,
        validations: { required: true },
      },
      {
        key: 'fcltyTypKey',
        label: 'DISBURSEMENT.FACILITY_TYPE',
        type: 'select',
        value: this.selectedFacility.fcltyTypKey,
        options$: this.pickListService.getOptions(PKL_GRP.FACILITY_TYPE),
        validations: { required: true },
      },
      {
        key: 'phase',
        label: 'DISBURSEMENT.PHASE_KEY',
        type: 'number',
        value: this.selectedFacility.phase,
      },
      {
        key: 'amount',
        label: 'DISBURSEMENT.APPROVED_AMOUNT',
        type: 'number',
        value: this.selectedFacility.amount,
      },
      {
        key: 'fcltyDsprdAmt',
        label: 'DISBURSEMENT.DISBURSED_AMOUNT',
        type: 'number',
        value: this.selectedFacility.fcltyDsprdAmt,
      },
      {
        key: 'date',
        label: 'DISBURSEMENT.APPROVAL_DATE',
        type: 'date',
        value: this.selectedFacility.date,
        validations: { required: true, noFuturDate: true },
      },
      {
        key: 'fcltyEndDt',
        label: 'DISBURSEMENT.END_DATE',
        type: 'date',
        value: this.selectedFacility.fcltyEndDt,
        validations: { required: true, futureDateOnly: true },
      },
      {
        key: 'dsbmntFlg',
        label: 'DISBURSEMENT.DISBURSEMENT_ALLOWED',
        type: 'select',
        value: this.selectedFacility.dsbmntFlg ?? 0,
        options$: [
          { label: 'COMMON.YES', value: 1 },
          { label: 'COMMON.NO', value: 0 },
        ],
        validations: { required: true },
      },
      {
        key: 'markupFlg',
        label: 'DISBURSEMENT.MARKUP_CHECK_IN',
        type: 'select',
        value: this.selectedFacility.markupFlg ?? 0,
        options$: [
          { label: 'COMMON.YES', value: 1 },
          { label: 'COMMON.NO', value: 0 },
        ],
        validations: { required: true },
      },
      {
        key: 'status',
        label: 'COMMON.STATUS',
        type: 'select',
        value: this.selectedFacility.status,
        options$: this.pickListService.getOptions(PKL_GRP.STATUS),
      },
      {
        key: 'spreadRate',
        label: 'DISBURSEMENT.SPREAD_RATE',
        type: 'number',
        value: this.selectedFacility.spreadRate,
      },
      {
        key: 'kiborResetPeriod',
        label: 'DISBURSEMENT.KIBOR_RESET_PERIOD',
        type: 'select',
        value: this.selectedFacility.kiborResetPeriod,
        options$: this.pickListService.getOptions(PKL_GRP.KIBOR_RESET_PERIOD),
        validations: { required: true },
      },

      {
        key: 'vkbrFlg',
        label: 'DISBURSEMENT.KIBOR_VARIABLE_RATE',
        type: 'select',
        value: this.selectedFacility.vkbrFlg ?? 0,
        options$: [
          { label: 'COMMON.YES', value: 1 },
          { label: 'COMMON.NO', value: 0 },
        ],
      },

      {
        key: 'vkbrRt',
        label: 'DISBURSEMENT.KIBOR_VARIABLE_RATE',
        type: 'number',
        value: this.selectedFacility.vkbrRt ?? null,
        validations: { min: 0 },
        hidden: !(this.selectedFacility.vkbrFlg === 1),
      },

      {
        key: 'vkbrStrtDt',
        label: 'DISBURSEMENT.KIBOR_START_DATE',
        type: 'date',
        value: this.selectedFacility.vkbrStrtDt ?? null,
        hidden: !(this.selectedFacility.vkbrFlg === 1),
      },
      {
        key: 'vkbrEndDt',
        label: 'DISBURSEMENT.KIBOR_END_DATE',
        type: 'date',
        value: this.selectedFacility.vkbrEndDt ?? null,
        hidden: !(this.selectedFacility.vkbrFlg === 1),
      },
    ];
  }
  isPaymentAllowed(schedule: any): boolean {
    if (!this.scheduleList?.length) return false;

    const firstPending = this.scheduleList.find((s) => s.remainingAmount > 0);
    if (!firstPending) return false;

    return schedule.pymtSchdSeq === firstPending.pymtSchdSeq;
  }

  onFacilityFieldChange(data: any): void {
    // ✅ Only handle vkbrFlg changes (since acrdCalcFlg is always 0)
    if (!('vkbrFlg' in data)) return;

    this.selectedFacility = {
      ...this.selectedFacility,
      ...data,
    };

    // ✅ Clear V-KIBOR details when vkbrFlg is disabled
    if (data.vkbrFlg === 0) {
      this.selectedFacility.vkbrRt = null;
      this.selectedFacility.vkbrStrtDt = null;
      this.selectedFacility.vkbrEndDt = null;
    }

    // ✅ Update field visibility based on vkbrFlg
    this.facilityPopupFields.forEach((field) => {
      if (['vkbrRt', 'vkbrStrtDt', 'vkbrEndDt'].includes(field.key)) {
        field.hidden = this.selectedFacility.vkbrFlg !== 1;
      }
    });
  }

  private getDays(): { label: string; value: number }[] {
    return Array.from({ length: 31 }, (_, i) => ({
      label: String(i + 1),
      value: i + 1,
    }));
  }

  saveFacility(): void {
    this.disbursementService.createFacility(this.selectedFacility).subscribe({
      next: (res: any) => {
        // ✅ HANDLE ERROR IN 200 RESPONSE
        if (res?.error) {
          this.toast.error(res.error); // popup error
          return;
        }

        // ✅ SUCCESS
        this.toast.success(res.message || 'DISBURSEMENT.SAVED_SUCCESSFULLY');
        this.closeAll();
        this.loadFacilities();
      },

      error: (err) => {
        console.error(err);
        this.toast.error('DISBURSEMENT.SOMETHING_WENT_WRONG');
      },
    });
  }

  // ================= FACILITY TABLE CONFIG =================
  isAnySelectedRestructured(): boolean {
    return this.selectedFacilities.some((f) => this.isRestructured(f));
  }
  facilityTableActions: TableAction[] = [
    {
      icon: '/public/1.png',
      onClick: (row: any) => this.openEditFacility(row),
      tooltip: 'DISBURSEMENT.EDIT_FACILITY',
      hide: (row) => this.isRestructured(row),
    },
    {
      icon: this.faInfoCircle,
      onClick: (row: any) => this.openViewFacility(row),
      class: 'h-8 w-8 text-blue-400    flex items-center justify-center text-[24px]',
      tooltip: 'DISBURSEMENT.VIEW_FACILITY',
      hide: (row) => !this.isRestructured(row),
    },
    {
      icon: '/public/2.png',
      onClick: (row: any) => this.openRepaymentList(row),
      tooltip: 'DISBURSEMENT.CONSOLIDATED_REPAYMENTS',
    },
    // {
    //   icon: '/public/3.png',
    //   onClick: (row: any) => this.openAccruedIncomeByClient(row),
    //   tooltip: 'Accrued Income (Client)',
    // },
    {
      icon: '/public/4.png',
      onClick: (row: any) => this.openAssuredIncom(row),
      tooltip: 'DISBURSEMENT.ACCRUED_INCOME_FACILITY_ACTION',
    },
    {
      label: 'RS',
      onClick: (row: any) => this.openDisbursementModal(row),
      class:
        'text-xs font-bold text-indigo-600 border border-indigo-200 px-2 py-1 rounded hover:bg-indigo-50',
      tooltip: 'DISBURSEMENT.TITLE',
    },
    {
      label: 'P',
      onClick: (row: any) => this.openPenaltyList(row),
      class:
        'text-xs font-bold text-red-600 border border-red-200 px-2 py-1 rounded hover:bg-red-50',
      tooltip: 'DISBURSEMENT.PENALTY_LIST',
    },
  ];
  openViewFacility(row: Facility): void {
    this.isCreateMode = false;

    this.selectedFacility = {
      ...row,
    };

    this.prepareFacilityPopupFields();

    // 🔒 Disable all fields
    this.facilityPopupFields = this.facilityPopupFields.map((f) => ({
      ...f,
      disabled: true,
    }));

    this.showEditFacility = true;
  }
  // ================= REPAYMENT ACTIONS =================
  repaymentPopupFields: DynamicFieldConfig[] = [];
  openPenaltyList(row: Facility): void {
    const fcltySeq = row.id;

    if (!fcltySeq) {
      this.toast.error('DISBURSEMENT.FACILITY_NOT_SELECTED');
      return;
    }

    this.loading = true;

    this.disbursementService.getPenaltyByFacility(fcltySeq).subscribe({
      next: (res: any) => {
        // ✅ Check for error in response body
        if (res?.error) {
          this.loading = false;
          this.toast.error(res.error);
          return;
        }
        const list = Array.isArray(res) ? res : res.data || [];

        this.penaltyList = list;

        this.showPenaltyModal = true;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.toast.error('DISBURSEMENT.FAILED_LOAD_PENALTY');
      },
    });
  }
  prepareRepaymentPopupFields(): void {
    this.repaymentPopupFields = [
      {
        key: 'date',
        label: 'COMMON.DATE',
        type: 'date',
        value: this.selectedRepayment.date,
        validations: { required: true },
      },
      {
        key: 'bank',
        label: 'DISBURSEMENT.BANK',
        type: 'select',
        value: this.selectedRepayment.bank,
        options$: this.banks.map((b) => ({ label: b, value: b })),
        validations: { required: true },
      },
      {
        key: 'transactionNo',
        label: 'DISBURSEMENT.TRANSACTION_NO',
        type: 'text',
        value: this.selectedRepayment.transactionNo,
        validations: { required: true },
      },
      {
        key: 'amount',
        label: 'COMMON.AMOUNT',
        type: 'number',
        value: this.selectedRepayment.amount,
        validations: { required: true, min: 1 },
      },
    ];
  }

  // Title for popup
  accruedIncomeTitle: string = 'DISBURSEMENT.ACCRUED_INCOME';

  getClientName(clientSeq?: number | null): string {
    if (!clientSeq) return 'N/A';
    const client = this.clients.find((c) => c.clientSeq === clientSeq);
    return client ? client.name : 'Unknown';
  }

  openAccruedIncomeByClient(row: Facility): void {
    console.log('Opening Accrued Income By Client. Row Data:', row);
    this.selectedFacility = { ...row };

    const clientName = this.getClientName(row.clientSeq);
    this.accruedIncomeTitle = 'DISBURSEMENT.ACCRUED_INCOME_CLIENT';

    // Refresh table config
    this.initAssuredIncomeTable(false);

    this.showAssuredIncom = true;
    this.loading = true;
    this.assuredIncomeData = [];

    const clientSeq = Number(row.clientSeq);

    // Fetch Accrued Income by Client Sequence
    if (clientSeq && !isNaN(clientSeq) && clientSeq > 0) {
      console.log('Fetching for ClientSeq:', clientSeq);
      this.disbursementService.getAccruedIncomeByClient(clientSeq).subscribe({
        next: (res: any) => {
          // ✅ Check for error in response body
          if (res?.error) {
            this.loading = false;
            this.toast.error(res.error);
            return;
          }
          const list = Array.isArray(res) ? res : res.data || [];
          this.assuredIncomeData = list;
          this.loading = false;
          console.log('Accrued Income (Client) Response:', list);
        },
        error: (err) => {
          console.error('Failed to load Accrued Income by Client', err);
          this.loading = false;
          this.toast.error('DISBURSEMENT.FAILED_LOAD_ACCRUED_INCOME');
        },
      });
    } else {
      console.warn('Invalid Client Seq:', row.clientSeq);
      this.loading = false;
      this.toast.error('DISBURSEMENT.NO_CLIENT_FOR_FACILITY');
    }
  }

  openRepaymentList(row: any): void {
    console.log('Loading repayments for:', row);
    this.selectedFacility = { ...row }; // Store context
    this.loading = true;
    const id = row.id || row.fcltySeq;

    this.disbursementService.getConsolidatedPayments(id).subscribe({
      next: (res: any) => {
        const list = Array.isArray(res) ? res : res.data || [];
        this.repayments = list.map((item: any, index: number) => ({
          id: index,
          month: item.month,
          monthYearOfCalc: item.month,
          paymentDate: item.paymentDate,
          totalAmount: item.totalAmount,
          totalDueAmt: item.totalDueAmt,
          numberOfInstallments: item.numberOfInstallments,
          stsKey: item.stsKey,
        }));
        this.showRepaymentList = true;
        this.loading = false;
      },
      error: (err) => {
        console.error('Failed to load repayments', err);
        this.loading = false;
        this.toast.error('DISBURSEMENT.FAILED_LOAD_REPAYMENTS');
      },
    });
  }

  addNewRepayment(row: Facility): void {
    this.selectedFacility = row;
    this.isRepaymentCreateMode = true;
    this.selectedRepayment = this.createEmptyRepayment();
    this.prepareRepaymentPopupFields();
    this.showEditRepayment = true;
  }

  openEditRepayment(row: Repayment): void {
    this.isRepaymentCreateMode = false;
    this.selectedRepayment = { ...row };
    this.prepareRepaymentPopupFields();
    this.showEditRepayment = true;
  }

  onRepaymentPopupSave = (data: any) => {
    this.selectedRepayment = { ...this.selectedRepayment, ...data };
    const index = this.repayments.findIndex((r) => r.id === this.selectedRepayment.id);
    if (index > -1) {
      this.repayments[index] = { ...this.selectedRepayment };
    } else {
      this.repayments.push({ ...this.selectedRepayment });
    }
    this.showEditRepayment = false;
  };

  // ================= REPAYMENT PAYMENT (Full Only) =================
  // Endpoint: POST /mark-schedule-paid/{fcltySeq}/{monthYear}

  openRepaymentPayment(row: any): void {
    console.log('Open Repayment Payment for:', row);
    this.selectedRepaymentPayment = {
      month: row.month,
      monthYearOfCalc: row.monthYearOfCalc || row.month,
      fcltySeq: this.selectedFacility?.id,
      totalAmount: row.totalAmount,
      trxId: '',
      chqNum: '',
      pstngDt: new Date().toISOString().split('T')[0],
    };

    this.repaymentPaymentPopupFields = [
      {
        key: 'trxId',
        label: 'DISBURSEMENT.TRANSACTION_ID',
        type: 'text' as any,
        value: '',
        validations: { required: true },
      },
      {
        key: 'chqNum',
        label: 'DISBURSEMENT.CHEQUE_NUMBER',
        type: 'text' as any,
        value: '',
        validations: { required: true },
      },
      {
        key: 'pstngDt',
        label: 'DISBURSEMENT.POSTING_DATE',
        type: 'date' as any,
        value: this.selectedRepaymentPayment.pstngDt,
        validations: { required: true },
      },
    ];

    this.showRepaymentPaymentModal = true;
  }

  onRepaymentPaymentPopupSave = (data: any) => {
    const fcltySeq = Number(this.selectedRepaymentPayment.fcltySeq);

    if (!fcltySeq) {
      this.toast.error('DISBURSEMENT.FACILITY_INFO_NOT_AVAILABLE');
      return;
    }

    this.loading = true;

    const payload = {
      fcltySeq: fcltySeq,
      trxId: data.trxId,
      chqNum: data.chqNum,
      pstngDt: new Date(data.pstngDt).toISOString(),
    };

    const formattedMonth = this.formatToYearMonth(this.selectedRepaymentPayment.monthYearOfCalc);

    console.log('Processing Repayment Payment:', { fcltySeq, formattedMonth, payload });

    this.disbursementService.markSchedulePaid(fcltySeq, formattedMonth, payload).subscribe({
      next: (res) => {
        this.loading = false;
        if (res.error) {
          this.toast.error(res.error);
          return;
        }
        this.toast.success(res.message || 'DISBURSEMENT.REPAYMENT_MARKED_SUCCESS');
        const amt = Number(this.selectedRepaymentPayment.totalAmount);
        if (amt) this.toast.info(
            this.translate.instant('DISBURSEMENT.AMOUNT_PAID', { amount: amt.toLocaleString() }),
          );
        this.showRepaymentPaymentModal = false;
        this.openRepaymentList(this.selectedFacility); // Reload repayments
      },
      error: (err) => {
        this.loading = false;
        console.error('Repayment payment failed', err);
        const errMsg = err?.error?.message || err?.error?.error || 'DISBURSEMENT.FAILED_PROCESS_REPAYMENT';
        this.toast.error(errMsg);
      },
    });
  };

  // ================= ASSURED INCOME CONFIG =================
  assuredIncomeTableColumns: TableColumn[] = [];
  accruedIncomeDetailTableColumns: TableColumn[] = [];

  getFacilityMap(): Record<number, string> {
    const map: Record<number, string> = {};
    this.facilities.forEach((f) => {
      map[f.id] = f.facilityName;
    });
    return map;
  }

  assuredIncomeTableActions: TableAction[] = [];
  accruedIncomeDetailTableActions: TableAction[] = [];
  showAccruedPaymentModal = false;
  selectedAccruedPayment: any = {};
  accruedPaymentPopupFields: DynamicFieldConfig[] = [];

  // ================= ACCRUED INCOME (CONSOLIDATED) =================
  accruedIncomeByMonth: any[] = [];
  expandedAccruedMonths = new Set<string>();
  accruedIncomeSummary: any = {
    facilityName: '',
    totalAccrued: 0,
    paidAccrued: 0,
    remainingAccrued: 0,
  };
  showAccruedIncomePaymentModal = false;
  selectedAccruedIncomePayment: any = {};
  selectedAccruedIncomePaymentRows: any[] = [];
  accruedIncomePaymentPopupFields: DynamicFieldConfig[] = [];
  accruedIncomePaymentFormData: any = {};
  selectedAccruedMonthYears = new Set<string>();

  initAssuredIncomeTable(showActions: boolean = false) {
    this.assuredIncomeTableColumns = [
      { label: 'COMMON.DATE', key: 'monthYearOfCalc', type: 'text' },
      {
        label: 'DISBURSEMENT.FACILITY',
        key: 'fcltySeq',
        type: 'map',
        map: this.getFacilityMap(),
      },
      { label: 'DISBURSEMENT.OUTSTANDING_DISBURSEMENT', key: 'dsbmntOutStndng', type: 'currency' },
      { label: 'DISBURSEMENT.SPREAD', key: 'sprdRt', type: 'text' },
      { label: 'DISBURSEMENT.KIBOR', key: 'kbrRt', type: 'text' },
      { label: 'DISBURSEMENT.ACCRUED_AMOUNT', key: 'accrudAmnt', type: 'currency' },
      {
        label: 'COMMON.STATUS',
        key: 'stsKey',
        type: 'map',
        map: this.getPicklistMap(PKL_GRP.DISBURSEMENT_PAYMENT_TYPE),
      },
    ];

    if (showActions) {
      this.assuredIncomeTableActions = [
        {
          icon: faMoneyBill,
          class:
            'h-8 w-8 bg-green-100 text-green-600 rounded-full flex items-center justify-center',
          onClick: (row: any) => this.openAccruedPayment(row),
          tooltip: 'DISBURSEMENT.MARK_AS_PAID',
        },
      ];
    } else {
      this.assuredIncomeTableActions = [];
    }
  }

  initAccruedIncomeConsolidatedTable() {
    // Monthly summary columns (outer rows)
    this.assuredIncomeTableColumns = [
      { label: 'DISBURSEMENT.MONTH_YEAR', key: 'monthYear', type: 'text' },
      { label: 'DISBURSEMENT.TOTAL_ACCRUED', key: 'totalAccrued', type: 'currency' },
      { label: 'DISBURSEMENT.PAID_ACCRUED', key: 'paidAccrued', type: 'currency' },
      { label: 'DISBURSEMENT.REMAINING', key: 'remainingAccrued', type: 'currency' },
    ];

    // Detail columns (inner expandable rows)
    this.accruedIncomeDetailTableColumns = [
      {
        label: 'DISBURSEMENT.FACILITY',
        key: 'fcltySeq',
        type: 'map',
        map: this.getFacilityMap(),
      },
      { label: 'DISBURSEMENT.OUTSTANDING_DISBURSEMENT', key: 'dsbmntOutStndng', type: 'currency' },
      { label: 'DISBURSEMENT.ACCRUED_AMOUNT', key: 'accrudAmnt', type: 'currency' },
      { label: 'DISBURSEMENT.SPREAD_RATE', key: 'sprdRt', type: 'text' },
      { label: 'DISBURSEMENT.KIBOR_RATE', key: 'kbrRt', type: 'text' },
      {
        label: 'COMMON.STATUS',
        key: 'stsKey',
        type: 'map',
        map: this.getPicklistMap(PKL_GRP.DISBURSEMENT_PAYMENT_TYPE),
      },
    ];

    this.accruedIncomeDetailTableActions = [];
  }

  canPayAccruedMonth(monthGroup: any): boolean {
    return Number(monthGroup?.remainingAccrued ?? 0) > 0;
  }

  isAccruedMonthSelected(monthYear: string): boolean {
    return this.selectedAccruedMonthYears.has(monthYear);
  }

  toggleAccruedMonthSelection(monthGroup: any, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    const key = monthGroup?.monthYear;
    if (!key) return;

    if (checked) {
      this.selectedAccruedMonthYears.add(key);
    } else {
      this.selectedAccruedMonthYears.delete(key);
    }
  }

  get selectedAccruedMonthCount(): number {
    return this.selectedAccruedMonthYears.size;
  }

  assuredIncomeData: any[] = [];

  openAssuredIncom(row: Facility): void {
    this.selectedFacility = { ...row };

    this.accruedIncomeTitle = 'DISBURSEMENT.ACCRUED_INCOME_FACILITY';

    // Initialize consolidated table (monthly grouping)
    this.initAccruedIncomeConsolidatedTable();

    this.showAssuredIncom = true;
    this.loading = true;
    this.accruedIncomeByMonth = [];
    this.expandedAccruedMonths.clear();
    this.selectedAccruedMonthYears.clear();

    this.disbursementService.getAccruedIncome(row.id).subscribe({
      next: (res: any) => {
        // ✅ Check for error in response body
        if (res?.error) {
          this.loading = false;
          this.toast.error(res.error);
          return;
        }
        // Response is already grouped by monthYear in API
        const groupedData = Array.isArray(res) ? res : res.data || [];

        // Process grouped data
        this.accruedIncomeByMonth = groupedData.map((monthGroup: any) => ({
          monthYear: monthGroup.monthYear,
          totalAccrued: monthGroup.totalAccrued || 0,
          paidAccrued: monthGroup.paidAccrued || 0,
          remainingAccrued: monthGroup.remainingAccrued || 0,
          details: monthGroup.details || [],
          isExpanded: false,
        }));

        // Calculate overall summary
        const allMonths = this.accruedIncomeByMonth;
        this.accruedIncomeSummary = {
          facilityName: row.facilityName,
          totalAccrued: allMonths.reduce((sum, m) => sum + (m.totalAccrued || 0), 0),
          paidAccrued: allMonths.reduce((sum, m) => sum + (m.paidAccrued || 0), 0),
          remainingAccrued: allMonths.reduce((sum, m) => sum + (m.remainingAccrued || 0), 0),
        };

        this.loading = false;
        console.log('Accrued Income (Consolidated):', this.accruedIncomeByMonth);
      },
      error: (err) => {
        console.error('Failed to load Accrued Income', err);
        this.loading = false;
        this.toast.error('DISBURSEMENT.FAILED_LOAD_ACCRUED_INCOME');
      },
    });
  }

  toggleAccruedMonth(monthYear: string): void {
    if (this.expandedAccruedMonths.has(monthYear)) {
      this.expandedAccruedMonths.delete(monthYear);
    } else {
      this.expandedAccruedMonths.add(monthYear);
    }
  }

  isAccruedMonthExpanded(monthYear: string): boolean {
    return this.expandedAccruedMonths.has(monthYear);
  }

  openAccruedPayment(row: any): void {
    this.selectedAccruedPayment = {
      fcltySeq: row.fcltySeq || this.selectedFacility.id,
      monthYear: row.monthYearOfCalc,
    };

    this.showAccruedPaymentModal = true;
  }

  confirmAccruedPayment(): void {
    const payload = {
      fcltySeq: Number(this.selectedAccruedPayment.fcltySeq),
      monthYear: this.selectedAccruedPayment.monthYear,
    };

    this.disbursementService.markAccruedPaidConsolidatedPayment(payload).subscribe({
      next: (res) => {
        if (res.error) {
          this.toast.error(res.error);
          return;
        }
        this.toast.success('DISBURSEMENT.PAYMENT_MARKED_SUCCESS');
        this.showAccruedPaymentModal = false;
      },
      error: () => {
        this.toast.error('DISBURSEMENT.FAILED_MARK_PAYMENT');
      },
    });
  }

  // ================= ACCRUED INCOME PAYMENT (Consolidated Modal) =================
  openAccruedIncomePayment(row?: any): void {
    const targetRows = row
      ? [row]
      : this.accruedIncomeByMonth.filter(
          (m: any) => this.selectedAccruedMonthYears.has(m.monthYear) && this.canPayAccruedMonth(m),
        );

    if (!targetRows.length) {
      this.toast.error('DISBURSEMENT.SELECT_AT_LEAST_ONE_MONTH');
      return;
    }

    const firstRow = targetRows[0];
    console.log('Open Accrued Income Payment for:', targetRows);

    const monthYearOfCalc =
      firstRow.monthYear ||
      firstRow.monthYearOfCalc ||
      (this.accruedIncomeByMonth.length > 0
        ? this.accruedIncomeByMonth[0].monthYear
        : new Date().toISOString().slice(0, 7));

    this.selectedAccruedIncomePayment = {
      accrudMrkUpSeq: firstRow.accrudMrkUpSeq,
      fcltySeq: firstRow.fcltySeq || this.selectedFacility?.id,
      monthYear: monthYearOfCalc,
      accrudAmnt: firstRow.remainingAccrued || firstRow.totalAccrued || firstRow.accrudAmnt || 0,
      trxId: '',
      chqNum: '',
      pstngDt: new Date().toISOString().split('T')[0],
    };

    this.selectedAccruedIncomePaymentRows = targetRows.map((r: any) => ({
      fcltySeq: r.fcltySeq || this.selectedFacility?.id,
      monthYear: r.monthYear || r.monthYearOfCalc,
      accrudAmnt: r.remainingAccrued || r.totalAccrued || r.accrudAmnt || 0,
      accrudMrkUpSeq: r.accrudMrkUpSeq,
    }));

    this.accruedIncomePaymentFormData = {
      isPartial: false,
      amount: null,
      trxId: '',
      chqNum: '',
      pstngDt: this.selectedAccruedIncomePayment.pstngDt,
    };
    this.accruedIncomePaymentPopupFields = this.buildAccruedIncomePaymentFields(false);

    this.showAccruedIncomePaymentModal = true;
  }

  private get accruedIncomePaymentTotal(): number {
    return this.selectedAccruedIncomePaymentRows.reduce(
      (sum: number, r: any) => sum + (Number(r.accrudAmnt) || 0),
      0,
    );
  }

  // "YYYY-MM" as the API expects; accepts "YYYY-MM" or "March 2026" style values.
  private toYearMonthValue(value: string): string {
    if (!value) return '';
    return /^\d{4}-\d{2}$/.test(value) ? value : this.formatToYearMonth(value);
  }

  private buildAccruedIncomePaymentFields(isPartial: boolean): DynamicFieldConfig[] {
    return [
      {
        key: 'isPartial',
        label: 'DISBURSEMENT.PARTIAL_PAYMENT',
        type: 'checkbox',
        placeholder: 'DISBURSEMENT.PAY_PARTIALLY',
        value: isPartial,
        // Partial payment is for one accrued month, so only offer it when a single month is being paid
        hidden: this.selectedAccruedIncomePaymentRows.length > 1,
      },
      {
        key: 'amount',
        label: 'COMMON.AMOUNT',
        type: 'number',
        value: null,
        hidden: !isPartial,
        disabled: !isPartial,
        validations: {
          required: true,
          min: 1,
          max: this.accruedIncomePaymentTotal || undefined,
          nonNegativeDecimal: true,
        },
      },
      {
        key: 'trxId',
        label: 'DISBURSEMENT.TRANSACTION_ID',
        type: 'text' as any,
        value: '',
        validations: { required: true },
      },
      {
        key: 'chqNum',
        label: 'DISBURSEMENT.CHEQUE_NUMBER',
        type: 'text' as any,
        value: '',
        validations: { required: true },
      },

      {
        key: 'pstngDt',
        label: 'DISBURSEMENT.POSTING_DATE',
        type: 'date' as any,
        value: this.selectedAccruedIncomePayment.pstngDt,
        validations: { required: true },
      },
    ];
  }

  // Show / hide the Amount field when the "Partial Payment" checkbox is toggled.
  // Fields are rebuilt from the current form data so entered values are kept.
  onAccruedIncomePaymentFormChange(val: any): void {
    const wasPartial = !!this.accruedIncomePaymentFormData?.isPartial;
    this.accruedIncomePaymentFormData = { ...this.accruedIncomePaymentFormData, ...val };
    const isPartial = !!val?.isPartial;

    if (isPartial !== wasPartial) {
      if (!isPartial) this.accruedIncomePaymentFormData.amount = null;
      this.accruedIncomePaymentPopupFields = this.buildAccruedIncomePaymentFields(isPartial);
    }
  }

  private submitAccruedPartialPayment(data: any): void {
    // The accrued month being paid (row from consolidated-view, e.g. monthYear "2026-07")
    const target = this.selectedAccruedIncomePaymentRows[0] || this.selectedAccruedIncomePayment;
    const fcltySeq = Number(target?.fcltySeq || this.selectedFacility?.id);

    if (!fcltySeq) {
      this.toast.error('DISBURSEMENT.FACILITY_INFO_NOT_AVAILABLE');
      return;
    }

    // Posting date from the picker + current local time, e.g. "2026-10-15T10:30:00"
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const pstngDt = `${data.pstngDt}T${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;

    const payload = {
      fcltySeq,
      monthYear: this.toYearMonthValue(target?.monthYear),
      amount: Number(data.amount),
      trxId: data.trxId,
      chqNum: data.chqNum,
      pstngDt,
    };

    this.loading = true;
    this.disbursementService.markAccruedPaidPartialPayment(payload).subscribe({
      next: (res: any) => {
        this.loading = false;
        if (res?.error) {
          this.toast.error(res.error);
          return;
        }
        this.toast.success('DISBURSEMENT.PARTIAL_ACCRUED_PAID_SUCCESS');
        this.showAccruedIncomePaymentModal = false;
        this.selectedAccruedIncomePaymentRows = [];
        this.selectedAccruedMonthYears.clear();
        this.openAssuredIncom(this.selectedFacility);
      },
      error: (err: any) => {
        this.loading = false;
        this.toast.error(
          err?.error?.message || err?.error?.error || 'DISBURSEMENT.FAILED_PROCESS_PARTIAL_PAYMENT',
        );
      },
    });
  }

  onAccruedIncomePaymentPopupSave = (data: any) => {
    if (data?.isPartial) {
      this.submitAccruedPartialPayment(data);
      return;
    }

    const targets =
      this.selectedAccruedIncomePaymentRows.length > 0
        ? this.selectedAccruedIncomePaymentRows
        : [this.selectedAccruedIncomePayment];

    if (!targets.length) {
      this.toast.error('DISBURSEMENT.NO_ACCRUED_MONTH_SELECTED');
      return;
    }

    this.loading = true;

    from(targets)
      .pipe(
        concatMap((target: any) => {
          const fcltySeq = Number(target.fcltySeq || this.selectedFacility?.id);

          if (!fcltySeq) {
            return of({
              success: false,
              target,
              message: 'DISBURSEMENT.FACILITY_INFO_NOT_AVAILABLE',
            });
          }

          let monthYear = target.monthYear;
          if (monthYear && monthYear.length > 7) {
            monthYear = this.formatToYearMonth(monthYear);
          }

          const payload = {
            facilitySeq: fcltySeq,
            monthYear: monthYear,
            paymentDetails: {
              trxId: data.trxId,
              chqNum: data.chqNum,
              pstngDt: new Date(data.pstngDt).toISOString(),
              fcltySeq: fcltySeq,
            },
          };

          return this.disbursementService.markAccruedPaidConsolidatedPayment(payload).pipe(
            map((res: any) => ({
              success: !res?.error,
              target,
              message: res?.error || res?.message || '',
            })),
            catchError((err: any) =>
              of({
                success: false,
                target,
                message:
                  err?.error?.message ||
                  err?.error?.error ||
                  'DISBURSEMENT.FAILED_PROCESS_ACCRUED_PAYMENT',
              }),
            ),
          );
        }),
        toArray(),
      )
      .subscribe((results: any[]) => {
        this.loading = false;

        const successCount = results.filter((r) => r.success).length;
        const failCount = results.length - successCount;

        if (successCount > 0 && failCount === 0) {
          this.toast.success(
            successCount === 1
              ? 'DISBURSEMENT.ACCRUED_PAYMENT_MARKED_SUCCESS'
              : this.translate.instant('DISBURSEMENT.ACCRUED_MONTHS_PAID', { count: successCount }),
          );
        } else if (successCount > 0 && failCount > 0) {
          this.toast.success(
            this.translate.instant('DISBURSEMENT.MONTHS_PAID', { count: successCount }),
          );
          this.toast.error(
            this.translate.instant('DISBURSEMENT.MONTHS_FAILED', { count: failCount }),
          );
        } else {
          const firstError = results.find((r) => !r.success)?.message;
          this.toast.error(firstError || 'DISBURSEMENT.FAILED_PROCESS_ACCRUED_PAYMENT');
        }

        if (successCount > 0) {
          this.showAccruedIncomePaymentModal = false;
          this.selectedAccruedIncomePaymentRows = [];
          this.selectedAccruedMonthYears.clear();
          this.openAssuredIncom(this.selectedFacility);
        }
      });
  };

  closeAll(): void {
    this.showEditFacility = false;
    this.showRepaymentList = false;
    this.showEditRepayment = false;
    this.showAssuredIncom = false;

    this.isCreateMode = false;
    this.isRepaymentCreateMode = false;
    this.showDisbursementModal = false;
    this.showScheduleList = false;
    this.resetScheduleModalState();
  }

  // ================= DISBURSEMENT ACTIONS =================

  prepareDisbursementPopupFields(): void {
    this.disbursementPopupFields = [
      {
        key: 'stsKey',
        label: 'COMMON.STATUS',
        type: 'select',
        value: this.selectedDisbursement.stsKey,
        options$: this.pickListService.getOptions(PKL_GRP.DISBURSEMENT_PAYMENT_TYPE),
        validations: { required: true },
      },
      {
        key: 'dsbmntDt',
        label: 'DISBURSEMENT.DISBURSEMENT_DATE',
        type: 'date',
        value: this.selectedDisbursement.dsbmntDt,
        validations: { required: true, noFuturDate: true },
      },

      {
        key: 'strtDt',
        label: 'DISBURSEMENT.FIRST_INSTALLMENT_DATE',
        type: 'date',
        value: this.selectedDisbursement.strtDt,
        validations: { required: true, min: 1 },
      },
      {
        key: 'endDt',
        label: 'DISBURSEMENT.INSTALLMENT_END_DATE',
        type: 'date',
        value: this.selectedDisbursement.dsbmntEndDt,
        validations: { required: true, min: 1 },
      },
      {
        key: 'dsbmntAmt',
        label: 'DISBURSEMENT.DISBURSEMENT_AMOUNT',
        type: 'number',
        value: this.selectedDisbursement.dsbmntAmt,
        validations: { required: true, min: 1 },
      },

      {
        key: 'instFrqncy',
        label: 'DISBURSEMENT.FREQUENCY',
        type: 'select',
        value: this.selectedDisbursement.instFrqncy,
        options$: this.pickListService.getOptions(PKL_GRP.DISBURSEMENT_FREQUENCY),
        validations: { required: true },
      },
    ];
  }

  openDisbursementModal(row: Facility): void {
    // 1. Set current facility
    this.selectedFacility = { ...row };

    // 2. Initialize Table Config (Dynamic Maps need fresh data)
    this.initTableConfig();

    // 3. Fetch existing disbursements
    this.disbursementService.getDisbursementsByFacility(row.id).subscribe({
      next: (res: any) => {
        const list = Array.isArray(res) ? res : res.data || [];
        this.disbursementList = list;

        if (this.disbursementList.length === 0) {
          // Case 3: No disbursements -> Open Form directly
          this.onAddNewDisbursement();
        } else {
          // Case 4: Disbursements exist -> Show List
          this.showDisbursementList = true;
        }
      },
      error: (err) => {
        console.error('Failed to fetch disbursements', err);
        // Fallback or error handling? For now, maybe just open form?
        // this.onAddNewDisbursement();
      },
    });
  }

  loadScheduleList(): void {
    const dsbmntSeq =
      this.selectedSchedulePayment?.dsbmntSeq || this.selectedDisbursement?.dsbmntSeq;
    if (!dsbmntSeq) {
      console.warn('loadScheduleList: dsbmntSeq not set, skipping reload');
      return;
    }
    this.disbursementService.getPaymentDetails(dsbmntSeq).subscribe({
      next: (res: any) => {
        // ✅ Check for error in response body
        if (res?.error) {
          this.resetScheduleModalState();
          this.toast.error(res.error);
          return;
        }
        this.paymentDetails = res;

        // Map schedules to legacy scheduleList shape for edit/save compatibility
        this.scheduleList = res.schedules.map((s: any) => ({
          pymtSchdSeq: s.pymtSchdSeq,
          instNum: s.instNum,
          dueDt: s.dueDate,
          dueAmt: s.dueAmount,
          paidAmt: s.paidAmount,
          remainingAmount: s.remainingAmount,
          stsKey: s.stsKey,
          status: s.status,
          paymentCount: s.paymentCount,
          payments: s.payments,
          dsbmntSeq: res.dsbmntSeq,
          fcltySeq: res.fcltySeq,
        }));

        // Build summary
        const pct =
          res.disbursementAmount > 0
            ? Math.round((res.totalPaidAmount / res.disbursementAmount) * 100)
            : 0;
        this.disbursementSummary = {
          facilityName: res.facilityName,
          disbursementDate: res.disbursementDate,
          disbursementAmount: res.disbursementAmount,
          totalSchedules: res.totalSchedules,
          paidSchedules: res.paidSchedules,
          pendingSchedules: res.pendingSchedules,
          totalPaidAmount: res.totalPaidAmount,
          totalRemainingAmount: res.totalRemainingAmount,
          fullyPaid: res.fullyPaid,
          progressPercentage: pct,
        };

        console.log('Payment Details Loaded:', res);
      },
      error: (err) => {
        this.resetScheduleModalState();
        console.error('Failed to load payment details', err);
        this.toast.error('DISBURSEMENT.FAILED_LOAD_PAYMENT_SCHEDULE');
      },
    });
  }

  togglePaymentHistory(pymtSchdSeq: number): void {
    if (this.expandedScheduleIds.has(pymtSchdSeq)) {
      this.expandedScheduleIds.delete(pymtSchdSeq);
    } else {
      this.expandedScheduleIds.add(pymtSchdSeq);
    }
  }

  openSchedule(disbursement: any): void {
    console.log('Open Schedule for:', disbursement);
    this.resetScheduleModalState();
    this.selectedDisbursement = { ...disbursement };

    this.initTableConfig();

    this.showScheduleList = true;
    this.loadScheduleList();
  }

  private formatPhase(phase: number): string {
    if (!phase) return 'N/A';

    const mod10 = phase % 10;
    const mod100 = phase % 100;

    if (mod10 === 1 && mod100 !== 11) return `${phase}st`;
    if (mod10 === 2 && mod100 !== 12) return `${phase}nd`;
    if (mod10 === 3 && mod100 !== 13) return `${phase}rd`;

    return `${phase}th`;
  }
  // ================= SCHEDULE EDIT LOGIC =================

  openEditSchedule(row: any): void {
    this.selectedSchedule = { ...row };

    this.schedulePopupFields = [
      {
        key: 'dueAmt',
        label: 'DISBURSEMENT.DUE_AMOUNT',
        type: 'number',
        value: this.selectedSchedule.dueAmt ?? this.selectedSchedule.dueAmount,
        validations: { required: true, min: 1 },
      },
      {
        key: 'dueDt',
        label: 'DISBURSEMENT.DUE_DATE',
        type: 'date',
        value: this.fromIsoToDate(this.selectedSchedule.dueDt ?? this.selectedSchedule.dueDate),
        validations: { required: true },
      },
    ];

    this.showEditSchedule = true;
  }

  onSchedulePopupSave = (data: any) => {
    // Update local list
    const updatedSchedule = { ...this.selectedSchedule, ...data };

    // Find index and update
    // Assuming pymtSchdSeq is unique key.
    // If not, might need index from row? But backend data usually has ID.
    const index = this.scheduleList.findIndex((s) => s.pymtSchdSeq === updatedSchedule.pymtSchdSeq);

    if (index > -1) {
      this.scheduleList[index] = updatedSchedule;
      this.scheduleList = [...this.scheduleList]; // Trigger angular change detection if needed on table
      this.isScheduleDirty = true;
    }

    this.showEditSchedule = false;
  };

  // ================= SCHEDULE PAYMENT LOGIC =================
  showSchedulePaymentModal = false;
  selectedSchedulePayment: any = {};
  schedulePaymentPopupFields: DynamicFieldConfig[] = [];

  // ================= REPAYMENT PAYMENT LOGIC =================
  showRepaymentPaymentModal = false;
  selectedRepaymentPayment: any = {};
  repaymentPaymentPopupFields: DynamicFieldConfig[] = [];

  openSchedulePayment(row: any): void {
    console.log('Open Schedule Payment for:', row);
    this.selectedSchedulePayment = {
      pymtSchdSeq: row.pymtSchdSeq,
      monthYearOfCalc: row.monthYearOfCalc || row.dueDt || row.dueDate,
      fcltySeq:
        this.paymentDetails?.fcltySeq ||
        this.selectedFacility?.id ||
        row.fcltySeq ||
        this.selectedDisbursement?.fcltySeq,
      dsbmntSeq:
        row.dsbmntSeq || this.paymentDetails?.dsbmntSeq || this.selectedDisbursement?.dsbmntSeq,
      dueAmt: row.dueAmt ?? row.dueAmount,
      remainingAmount:
        row.remainingAmount ?? (row.dueAmt ?? row.dueAmount) - (row.paidAmt ?? row.paidAmount ?? 0),
      trxId: '',
      chqNum: '',
      sysEntryDt: new Date().toISOString().split('T')[0],
      pstngDt: new Date().toISOString().split('T')[0],
      paymentType: 'full',
      paymentAmount: row.remainingAmount ?? row.dueAmt ?? row.dueAmount,
      remarks: '',
    };

    this.schedulePaymentPopupFields = this.buildSchedulePaymentFields();
    this.showSchedulePaymentModal = true;
  }

  private buildSchedulePaymentFields(): DynamicFieldConfig[] {
    const s = this.selectedSchedulePayment;
    const isPartial = s.paymentType === 'partial';
    return [
      {
        key: 'paymentType',
        label: 'DISBURSEMENT.PAYMENT_TYPE',
        type: 'radio' as any,
        value: s.paymentType,
        gridClass: 'col-span-2',
        options$: [
          { label: 'DISBURSEMENT.FULL_PAYMENT', value: 'full' },
          { label: 'DISBURSEMENT.PARTIAL_PAYMENT', value: 'partial' },
        ],
        validations: { required: true },
      },
      ...(isPartial
        ? [
            {
              key: 'paymentAmount',
              label: 'DISBURSEMENT.PAYMENT_AMOUNT',
              type: 'number' as any,
              value: s.paymentAmount,
              gridClass: 'col-span-2',
              placeholder: this.translate.instant('DISBURSEMENT.ENTER_AMOUNT_DUE', {
                due: s.dueAmt?.toLocaleString() || 0,
              }),
              validations: { required: true, min: 0.01 },
            },
            {
              key: 'remarks',
              label: 'COMMON.REMARKS',
              type: 'textarea' as any,
              value: s.remarks,
              gridClass: 'col-span-2',
              placeholder: 'DISBURSEMENT.PARTIAL_REMARKS_PLACEHOLDER',
              validations: { required: false },
            },
          ]
        : []),
      {
        key: 'trxId',
        label: 'DISBURSEMENT.TRANSACTION_ID',
        type: 'text' as any,
        value: s.trxId,
        validations: { required: true },
      },
      {
        key: 'chqNum',
        label: 'DISBURSEMENT.CHEQUE_NUMBER',
        type: 'text' as any,
        value: s.chqNum,
        validations: { required: true },
      },

      {
        key: 'pstngDt',
        label: 'DISBURSEMENT.POSTING_DATE',
        type: 'date' as any,
        value: s.pstngDt,
        validations: { required: true },
      },
    ] as DynamicFieldConfig[];
  }

  // Called on every form value change from the payment popup
  onSchedulePaymentFieldChange(data: any): void {
    const newType = data?.paymentType;
    // Preserve current form values so they aren't lost on rebuild
    if (data) {
      if (data.trxId !== undefined) this.selectedSchedulePayment.trxId = data.trxId;
      if (data.chqNum !== undefined) this.selectedSchedulePayment.chqNum = data.chqNum;
      if (data.pstngDt !== undefined) this.selectedSchedulePayment.pstngDt = data.pstngDt;
      if (data.paymentAmount !== undefined)
        this.selectedSchedulePayment.paymentAmount = data.paymentAmount;
      if (data.remarks !== undefined) this.selectedSchedulePayment.remarks = data.remarks;
    }
    if (newType && newType !== this.selectedSchedulePayment.paymentType) {
      this.onPaymentTypeChange(newType);
    }
  }

  // Handle payment type change - update form fields dynamically
  onPaymentTypeChange(paymentType: string): void {
    this.selectedSchedulePayment.paymentType = paymentType;
    if (paymentType === 'full') {
      this.selectedSchedulePayment.paymentAmount = this.selectedSchedulePayment.dueAmt;
    }
    this.schedulePaymentPopupFields = this.buildSchedulePaymentFields();
  }

  private formatToYearMonth(value: string): string {
    if (!value) return '';
    // Check if checks "March 2026" or "March-2026"
    // Append '-01' to ensure date parsing works for "Month Year" or "YYYY-MM" if needed,
    // but usually "Month Year" parses fine.
    // If it's just "March", it defaults to current year (might be risky if checking historical).
    // Let's assume input has year, e.g. "March-2026" or "March 2026".
    const date = new Date(value);
    if (isNaN(date.getTime())) return value;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  }

  onSchedulePaymentPopupSave = (data: any) => {
    const paymentType = data.paymentType || 'full';

    if (paymentType === 'full') {
      const fcltySeq = Number(this.selectedSchedulePayment.fcltySeq);
      if (!fcltySeq) {
        this.toast.error('DISBURSEMENT.FACILITY_INFO_NOT_AVAILABLE');
        return;
      }
      this.loading = true;
      this.processFullPayment(data, fcltySeq);
    } else if (paymentType === 'partial') {
      const dsbmntSeq = Number(this.selectedSchedulePayment.dsbmntSeq);
      if (!dsbmntSeq) {
        this.toast.error('DISBURSEMENT.DISBURSEMENT_INFO_NOT_AVAILABLE');
        return;
      }
      this.loading = true;
      this.processPartialPayment(data, dsbmntSeq);
    }
  };

  // Direct submit from custom inline modal (reads from selectedSchedulePayment)
  submitSchedulePayment(): void {
    const s = this.selectedSchedulePayment;
    const isPartial = s.paymentType === 'partial';

    if (!s.trxId?.trim()) {
      this.toast.error('DISBURSEMENT.TRANSACTION_ID_REQUIRED');
      return;
    }
    if (!s.chqNum?.trim()) {
      this.toast.error('DISBURSEMENT.CHEQUE_NUMBER_REQUIRED');
      return;
    }

    if (!isPartial && !s.pstngDt) {
      this.toast.error('DISBURSEMENT.POSTING_DATE_REQUIRED');
      return;
    }

    this.loading = true;

    if (isPartial) {
      const dsbmntSeq = Number(s.dsbmntSeq);
      if (!dsbmntSeq) {
        this.toast.error('DISBURSEMENT.DISBURSEMENT_INFO_NOT_AVAILABLE');
        this.loading = false;
        return;
      }
      this.processPartialPayment(s, dsbmntSeq);
    } else {
      const fcltySeq = Number(s.fcltySeq);
      if (!fcltySeq) {
        this.toast.error('DISBURSEMENT.FACILITY_INFO_NOT_AVAILABLE');
        this.loading = false;
        return;
      }
      this.processFullPayment(s, fcltySeq);
    }
  }

  // Process Schedule Full Payment (PRSP) → POST /mark-schedule-paid-for-phase/{fcltySeq}/{monthYear}
  private processFullPayment(data: any, fcltySeq: number): void {
    const payload = {
      fcltySeq: fcltySeq,
      trxId: data.trxId,
      chqNum: data.chqNum,
      sysEntryDt: new Date().toISOString(),
      pstngDt: new Date(data.pstngDt).toISOString(),
    };

    const formattedMonth = this.formatToYearMonth(this.selectedSchedulePayment.monthYearOfCalc);

    console.log('Processing FULL Schedule Payment:', { fcltySeq, formattedMonth, payload });

    this.disbursementService.markSchedulePaidForPhase(fcltySeq, formattedMonth, payload).subscribe({
      next: (res) => {
        this.loading = false;
        if (res.error) {
          this.toast.error(res.error);
          return;
        }
        this.toast.success(res.message || 'DISBURSEMENT.FULL_PAYMENT_MARKED_SUCCESS');
        this.showSchedulePaymentModal = false;
        this.loadScheduleList();
      },
      error: (err) => {
        this.loading = false;
        console.error('Full payment failed', err);
        this.toast.error('DISBURSEMENT.FAILED_PROCESS_FULL_PAYMENT');
      },
    });
  }

  // Process Schedule Partial Payment → POST /process-payment/{dsbmntSeq}
  // Payload: paymentAmount, trxId, chqNum, pstngDt, fcltySeq, remarks
  private processPartialPayment(data: any, dsbmntSeq: number): void {
    const paymentAmount = Number(data.paymentAmount) || this.selectedSchedulePayment.dueAmt;
    const dueAmount = Number(this.selectedSchedulePayment.dueAmt);

    // Validate amount
    if (paymentAmount <= 0) {
      this.toast.error('DISBURSEMENT.PAYMENT_AMOUNT_GT_ZERO');
      this.loading = false;
      return;
    }

    if (paymentAmount > dueAmount) {
      this.toast.error(
        this.translate.instant('DISBURSEMENT.PAYMENT_AMOUNT_EXCEEDS_DUE', { due: dueAmount }),
      );
      this.loading = false;
      return;
    }

    const fcltySeq = Number(
      this.paymentDetails?.fcltySeq ||
        this.selectedSchedulePayment?.fcltySeq ||
        this.selectedFacility?.id ||
        0,
    );

    const payload = {
      paymentAmount: paymentAmount,
      trxId: data.trxId,
      chqNum: data.chqNum,
      pstngDt: new Date(data.pstngDt).toISOString(),
      fcltySeq: fcltySeq,
      remarks: data.remarks || '',
    };

    console.log('Processing PARTIAL Schedule Payment:', { dsbmntSeq, payload });

    this.disbursementService.processPartialPayment(dsbmntSeq, payload).subscribe({
      next: (res: any) => {
        this.loading = false;
        if (res.error) {
          this.toast.error(res.error);
          return;
        }

        // Show toasts
        const adj0 = res.adjustments?.[0];
        const paid = adj0?.adjustedAmount ?? res.totalPaidAmount;
        const remaining = adj0?.newRemainingAmount;
        this.toast.success(res.message || 'DISBURSEMENT.PAYMENT_PROCESSED_SUCCESS');
        if (paid != null && remaining != null) {
          this.toast.info(
            this.translate.instant('DISBURSEMENT.PAID_REMAINING_INFO', {
              paid: Number(paid).toLocaleString(),
              remaining: Number(remaining).toLocaleString(),
            }),
          );
        }
        if (res.disbursementFullyPaid) {
          this.toast.success('DISBURSEMENT.DISBURSEMENT_FULLY_SETTLED');
        }

        // Patch scheduleList immediately from response so table updates without waiting for API
        if (res.adjustments?.length) {
          res.adjustments.forEach((adj: any) => {
            const idx = this.scheduleList.findIndex((s: any) => s.pymtSchdSeq === adj.pymtSchdSeq);
            if (idx !== -1) {
              this.scheduleList[idx] = {
                ...this.scheduleList[idx],
                paidAmt: adj.newPaidAmount,
                paidAmount: adj.newPaidAmount,
                remainingAmount: adj.newRemainingAmount ?? 0,
                stsKey: adj.adjustmentType === 'FULL' ? 8 : this.scheduleList[idx].stsKey,
                status: adj.adjustmentType === 'FULL' ? 'Paid' : this.scheduleList[idx].status,
              };
            }
          });
          this.scheduleList = [...this.scheduleList];
        }

        // Ensure dsbmntSeq is set (fallback from response) then reload for full sync
        if (res.dsbmntSeq && !this.selectedDisbursement?.dsbmntSeq) {
          this.selectedDisbursement = { ...this.selectedDisbursement, dsbmntSeq: res.dsbmntSeq };
        }
        this.loadScheduleList();
        this.showSchedulePaymentModal = false;
      },
      error: (err: any) => {
        this.loading = false;
        console.error('Partial payment failed', err);
        const errMsg =
          err?.error?.message || err?.error?.error || 'DISBURSEMENT.FAILED_PROCESS_PARTIAL_PAYMENT';
        this.toast.error(errMsg);
      },
    });
  }

  saveAllSchedules(): void {
    const payload = {
      dsbmntSeq: this.paymentDetails?.dsbmntSeq || this.selectedDisbursement.dsbmntSeq,
      paymentSchedules: this.scheduleList.map((item) => ({
        pymtSchdSeq: item.pymtSchdSeq,
        dueAmt: Number(item.dueAmt ?? item.dueAmount),
        dueDt: new Date(item.dueDt ?? item.dueDate).toISOString(),
      })),
    };

    console.log('Saving Schedules:', payload);

    this.disbursementService.updatePaymentSchedules(payload).subscribe({
      next: (res: any) => {
        // Success
        if (res.error) {
          this.toast.error(res.error);
        } else {
          this.toast.success('DISBURSEMENT.PAYMENT_SCHEDULE_SAVED');
          this.showScheduleList = false;
        }
      },
      error: (err) => {
        console.error('Failed to save schedules API Error:', err);

        // Robust Error Extraction
        let msg = 'DISBURSEMENT.FAILED_UPDATE_PAYMENT_SCHEDULE';

        if (err?.error) {
          if (typeof err.error === 'string') {
            msg = err.error; // Backend returned raw string
          } else if (typeof err.error === 'object') {
            // Backend returned JSON object
            msg = err.error.error || err.error.message || JSON.stringify(err.error);
          }
        } else if (err?.message) {
          msg = err.message;
        }

        this.toast.error(msg);
      },
    });
  }

  onDisbrestmentAccourd(row: any): void {
    const dsbmntSeq = row?.dsbmntSeq || this.selectedDisbursement?.dsbmntSeq;

    if (!dsbmntSeq) {
      this.toast.error('DISBURSEMENT.DISBURSEMENT_NOT_SELECTED');
      return;
    }

    this.loading = true;

    this.disbursementService.getAccruedMarkupByDisbursement(dsbmntSeq).subscribe({
      next: (res: any) => {
        // ✅ Check for error in response body
        if (res?.error) {
          this.loading = false;
          this.toast.error(res.error);
          return;
        }
        const list = Array.isArray(res) ? res : res.data || [];

        this.accruedMarkupList = list;
        this.showAccruedMarkupTable = true;

        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.toast.error('DISBURSEMENT.FAILED_LOAD_ACCRUED_MARKUP');
      },
    });
  }

  openAccruedMarkupPayment(row: any): void {
    console.log('Open Accrued Markup Payment for:', row);

    const dsbmntSeq = row.dsbmntSeq || this.selectedDisbursement?.dsbmntSeq;

    this.selectedAccruedMarkupPayment = {
      monthYear: row.monthYearOfCalc || row.month,
      dsbmntSeq: dsbmntSeq,
      fcltySeq: row.fcltySeq || this.selectedFacility?.id,
      trxId: '',
      chqNum: '',
      pstngDt: new Date().toISOString().split('T')[0],
    };

    this.accruedMarkupPaymentPopupFields = [
      {
        key: 'trxId',
        label: 'DISBURSEMENT.TRANSACTION_ID',
        type: 'text' as any,
        value: '',
        validations: { required: true },
      },
      {
        key: 'chqNum',
        label: 'DISBURSEMENT.CHEQUE_NUMBER',
        type: 'text' as any,
        value: '',
        validations: { required: true },
      },
      {
        key: 'pstngDt',
        label: 'DISBURSEMENT.POSTING_DATE',
        type: 'date' as any,
        value: this.selectedAccruedMarkupPayment.pstngDt,
        validations: { required: true },
      },
    ];

    this.showAccruedMarkupPaymentModal = true;
  }

  onAccruedMarkupPaymentPopupSave = (data: any) => {
    const dsbmntSeq = Number(this.selectedAccruedMarkupPayment.dsbmntSeq);

    if (!dsbmntSeq) {
      this.toast.error('DISBURSEMENT.DISBURSEMENT_INFO_NOT_AVAILABLE');
      return;
    }

    const payload = {
      trxId: data.trxId,
      chqNum: data.chqNum,
      pstngDt: new Date(data.pstngDt).toISOString(),
      fcltySeq: Number(this.selectedAccruedMarkupPayment.fcltySeq) || 0,
    };

    let monthYear = this.selectedAccruedMarkupPayment.monthYear;
    if (monthYear && monthYear.length > 7) {
      monthYear = this.formatToYearMonth(monthYear);
    }

    this.loading = true;
    this.disbursementService
      .payAccruedMarkupByDisbursement(dsbmntSeq, monthYear, payload)
      .subscribe({
        next: (res: any) => {
          this.loading = false;
          if (res.error) {
            this.toast.error(res.error);
            return;
          }
          this.toast.success(res.message || 'DISBURSEMENT.PAYMENT_SUCCESSFULLY_MARKED');
          this.showAccruedMarkupPaymentModal = false;

          // Reload list directly using the sequence ID we already have
          this.onDisbrestmentAccourd({ dsbmntSeq: dsbmntSeq });
        },
        error: (err: any) => {
          this.loading = false;
          const msg = err?.error?.message || err?.error?.error || 'DISBURSEMENT.FAILED_PROCESS_PAYMENT';
          this.toast.error(msg);
        },
      });
  };

  onAddNewDisbursement(): void {
    this.selectedDisbursement = {
      dsbmntSeq: null,
      dsbmntDt: new Date().toISOString().split('T')[0],
      dsbmntEndDt: '',
      dsbmntAmt: 0,
      stsKey: null,
      instFrqncy: null,
      dsbmntFlg: 0,
      fcltySeq: this.selectedFacility.id,
    };

    this.prepareDisbursementPopupFields();
    this.showDisbursementModal = true;
  }

  onDisbursementPopupSave = (data: any) => {
    this.selectedDisbursement = { ...this.selectedDisbursement, ...data };

    const disbDate = new Date(this.selectedDisbursement.dsbmntDt).toISOString();
    const startDate = new Date(this.selectedDisbursement.strtDt).toISOString();
    const endDate = new Date(this.selectedDisbursement.endDt).toISOString();

    const payload = {
      dsbmntSeq: this.selectedDisbursement.dsbmntSeq,
      fcltySeq: this.selectedFacility.id,

      dsbmntDt: disbDate,
      strtDt: startDate,
      endDt: endDate,

      dsbmntAmt: Number(this.selectedDisbursement.dsbmntAmt),
      stsKey: Number(this.selectedDisbursement.stsKey),
      instFrqncy: Number(this.selectedDisbursement.instFrqncy),

      dsbmntFlg: Number(this.selectedDisbursement.dsbmntFlg),
      delFlg: 0,
    };

    this.disbursementService.createDisbursement(payload).subscribe({
      next: () => {
        this.showDisbursementModal = false;
        this.loadFacilities();

        this.disbursementService.getDisbursementsByFacility(this.selectedFacility.id).subscribe({
          next: (res: any) => {
            const list = Array.isArray(res) ? res : res.data || [];
            this.disbursementList = list;
            this.showDisbursementList = true;
          },
        });
      },
      error: (err) => console.error('Save disbursement failed', err),
    });
  };

  // ================= DISBURSEMENT DELETE LOGIC =================

  openDeleteConfirm(row: any): void {
    this.selectedDisbursementForDelete = { ...row };
    this.showDeleteConfirmModal = true;
  }

  confirmDeleteDisbursement(): void {
    if (!this.selectedDisbursementForDelete?.dsbmntSeq) {
      this.toast.error('DISBURSEMENT.DISBURSEMENT_ID_NOT_FOUND');
      return;
    }

    this.loading = true;
    const dsbmntSeq = this.selectedDisbursementForDelete.dsbmntSeq;

    this.disbursementService.deleteDisbursement(dsbmntSeq).subscribe({
      next: (res: any) => {
        this.loading = false;
        if (res?.error) {
          this.toast.error(res.error);
          return;
        }
        this.toast.success(res.message || 'DISBURSEMENT.DISBURSEMENT_DELETED');
        this.showDeleteConfirmModal = false;
        this.selectedDisbursementForDelete = null;
        // Reload the disbursement list
        this.loadFacilities();
        if (this.selectedFacility?.id) {
          this.disbursementService.getDisbursementsByFacility(this.selectedFacility.id).subscribe({
            next: (res: any) => {
              const list = Array.isArray(res) ? res : res.data || [];
              this.disbursementList = list;
            },
          });
        }
      },
      error: (err: any) => {
        this.loading = false;
        const msg = err?.error?.message || err?.error?.error || 'DISBURSEMENT.FAILED_DELETE_DISBURSEMENT';
        this.toast.error(msg);
      },
    });
  }

  cancelDeleteDisbursement(): void {
    this.showDeleteConfirmModal = false;
    this.selectedDisbursementForDelete = null;
  }
}
